import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tree, SideNav, SideNavGroup, SideNavItem, IconButton, Chip, Loader } from '@origo/origo-ui';
import type { Område } from '@origo/seksjon';
import {
    MapPinSimpleAreaIcon,
    MapTrifoldIcon,
    PencilLineIcon,
    FunnelIcon,
    GearSixIcon,
    FolderSimpleIcon,
    ListDashesIcon,
} from '@phosphor-icons/react';
import type { SeksjonContext } from '../types';
import styles from './NavPanel.module.css';
import { NavDragContext } from './NavPanelDragContext';
import type { NavDragContextValue, OmrådeDragTarget } from './NavPanelDragContext';
import { DelomradetypeNode, EgendefinertTree } from './NavPanelTreeItems';

export type { OmrådeDragTarget } from './NavPanelDragContext';

export interface DelområdegruppeView {
    id: string;
    objectId: number;
    navn: string;
    delomradetype: string;
    memberDelomradeIds: readonly string[];
}

interface NavPanelProps {
    planId: string;
    kommune: string;
    /** Synlige delområdetyper etter filtrering — det Tree-et rendrer. */
    areas: Område[];
    /** Sant mens delområdene fortsatt hentes — viser en spinner i treet. */
    loading?: boolean;
    /** Totalt antall delområdetyper i planen (før filtrering). */
    totalAreaCount?: number;
    /** Hvor mange av planens delområdetyper som er skjult av filteret. */
    hiddenInPlan?: number;
    egendefinerte?: Område[];
    delområdegrupper?: readonly DelområdegruppeView[];
    activeContext: SeksjonContext;
    onSelect: (context: SeksjonContext) => void;
    onToggleFilter?: () => void;
    /** Fjerner kun planens egne ekskluderte typer fra det globale filteret. */
    onClearPlanFilter?: () => void;
    filterOpen?: boolean;
    /** Kalt når brukeren slipper et egendefinert område over et mål og velger «Flytt hele området». */
    onMoveOmråde?: (omrade: Område, target: OmrådeDragTarget) => Promise<void>;
    /** Kalt når brukeren slipper et egendefinert område over et mål og velger «Flytt bare veiledningene». */
    onCopyVeiledninger?: (omrade: Område, target: OmrådeDragTarget) => void;
}

type NavKey = 'innstillinger' | 'plan' | 'delomrade-soner' | 'egendefinerte';

interface NavEntry {
    key: NavKey;
    label: string;
    icon: JSX.Element;
    expandable: boolean;
}

const NAV_ENTRIES: readonly NavEntry[] = [
    { key: 'innstillinger', label: 'Innstillinger', icon: <GearSixIcon size={20} weight="duotone" />, expandable: false },
    { key: 'plan', label: 'Planens veiledninger', icon: <MapPinSimpleAreaIcon size={20} weight="duotone" />, expandable: false },
    { key: 'delomrade-soner', label: 'Planens delområder og soner', icon: <MapTrifoldIcon size={20} weight="duotone" />, expandable: true },
    { key: 'egendefinerte', label: 'Egendefinerte områder', icon: <PencilLineIcon size={20} weight="duotone" />, expandable: true },
];

interface BuiltContextMap {
    map: Map<string, SeksjonContext>;
    /** delområde-LOKALID → gruppe-id (medlem-relasjonen). */
    memberToGruppe: Map<string, string>;
    /** delomradetype-key → gruppe-rader. */
    grupperByType: Map<string, DelområdegruppeView[]>;
    /** delområde-id → egendefinerte områder som har dette delområdet som forelder. */
    egendefinertByDelområde: Map<string, Område[]>;
    /** delomradetype-key → egendefinerte områder som har denne typen som forelder. */
    egendefinertByDelområdetype: Map<string, Område[]>;
}

function buildContextMap(
    areas: Område[],
    grupper: readonly DelområdegruppeView[],
    egendefinerte: readonly Område[],
    planId: string
): BuiltContextMap {
    const map = new Map<string, SeksjonContext>();
    const memberToGruppe = new Map<string, string>();
    const grupperByType = new Map<string, DelområdegruppeView[]>();
    const egendefinertByDelområde = new Map<string, Område[]>();
    const egendefinertByDelområdetype = new Map<string, Område[]>();

    for (const g of grupper) {
        if (!grupperByType.has(g.delomradetype)) grupperByType.set(g.delomradetype, []);
        grupperByType.get(g.delomradetype)!.push(g);
        for (const m of g.memberDelomradeIds) memberToGruppe.set(m, g.id);
        map.set(`gruppe:${g.id}`, {
            type: 'delomradegruppe',
            planId,
            gruppeId: g.id,
            delomradetype: g.delomradetype,
        });
    }

    for (const area of areas) {
        map.set(area.id, { type: 'delomradetype', planId, delomradetype: area.id });
        for (const child of area.children ?? []) {
            map.set(child.id, {
                type: 'delomrade',
                planId,
                omradeId: child.id,
                delomradetype: area.id,
            });
        }
    }

    for (const item of egendefinerte) {
        map.set(item.id, {
            type: 'omrade',
            planId,
            omradeId: item.id,
            omradeNavn: item.navn,
        });
        if (item.kind !== 'egendefinert') continue;
        if (item.parentType === 'Delområde' && item.parentId) {
            if (!egendefinertByDelområde.has(item.parentId)) {
                egendefinertByDelområde.set(item.parentId, []);
            }
            egendefinertByDelområde.get(item.parentId)!.push(item);
        } else if (item.parentType === 'Delområdetype' && item.parentId) {
            if (!egendefinertByDelområdetype.has(item.parentId)) {
                egendefinertByDelområdetype.set(item.parentId, []);
            }
            egendefinertByDelområdetype.get(item.parentId)!.push(item);
        }
    }

    return {
        map,
        memberToGruppe,
        grupperByType,
        egendefinertByDelområde,
        egendefinertByDelområdetype,
    };
}

function getActiveNavKey(ctx: SeksjonContext, omradeIdsUnderDelområde: ReadonlySet<string>): NavKey {
    if (ctx.type === 'innstillinger') return 'innstillinger';
    if (ctx.type === 'plan' || ctx.type === 'kommune') return 'plan';
    if (
        ctx.type === 'delomrade-admin' ||
        ctx.type === 'delomradetype' ||
        ctx.type === 'delomrade' ||
        ctx.type === 'delomradegruppe'
    ) {
        return 'delomrade-soner';
    }
    if (ctx.type === 'egendefinerte-admin') return 'egendefinerte';
    if (ctx.type === 'omrade') {
        // Områder med delområde-parent hører hjemme i delområde-treet, ikke
        // i egendefinerte-lista — så aktivt tre følger der det rendres.
        if (ctx.omradeId && omradeIdsUnderDelområde.has(ctx.omradeId)) {
            return 'delomrade-soner';
        }
        return 'egendefinerte';
    }
    return 'plan';
}

function buildEgendefinertHierarchy(items: Område[]): Område[] {
    const map = new Map<string, Område>();
    const roots: Område[] = [];
    for (const item of items) map.set(item.id, { ...item, children: [] });

    for (const item of map.values()) {
        if (item.parentType === 'Område' && item.parentId && map.has(item.parentId)) {
            map.get(item.parentId)!.children!.push(item);
        } else {
            roots.push(item);
        }
    }
    return roots;
}

/* ------------------------------------------------------------------ */
/*  NavPanel                                                            */
/* ------------------------------------------------------------------ */

export function NavPanel({
    planId,
    kommune,
    areas,
    loading = false,
    totalAreaCount,
    hiddenInPlan = 0,
    egendefinerte = [],
    delområdegrupper = [],
    activeContext,
    onSelect,
    onToggleFilter,
    onClearPlanFilter,
    filterOpen = false,
    onMoveOmråde,
    onCopyVeiledninger,
}: Readonly<NavPanelProps>) {
    const hasFilter = hiddenInPlan > 0;
    const totalForChip = totalAreaCount ?? areas.length + hiddenInPlan;
    const {
        map: contextMap,
        memberToGruppe,
        grupperByType,
        egendefinertByDelområde,
        egendefinertByDelområdetype,
    } = useMemo(
        () => buildContextMap(areas, delområdegrupper, egendefinerte, planId),
        [areas, delområdegrupper, egendefinerte, planId]
    );
    const omradeIdsUnderDelområde = useMemo(
        () =>
            new Set(
                egendefinerte
                    .filter((e) => e.parentType === 'Delområde' || e.parentType === 'Delområdetype')
                    .map((e) => e.id)
            ),
        [egendefinerte]
    );
    const egendefinertTree = useMemo(
        // Områder med delområde- eller delområdetype-parent rendres i delområde-treet, ikke her.
        // kind-filteret holder delomradegruppe-member-rader (med parentType='Område') ute.
        () =>
            buildEgendefinertHierarchy(
                egendefinerte.filter(
                    (e) =>
                        e.kind === 'egendefinert' &&
                        e.parentType !== 'Delområde' &&
                        e.parentType !== 'Delområdetype'
                )
            ),
        [egendefinerte]
    );
    const activeNavKey = getActiveNavKey(activeContext, omradeIdsUnderDelområde);
    const treeActiveId =
        activeContext.type === 'delomradetype' ? (activeContext.delomradetype ?? '') :
            activeContext.type === 'delomrade' ? (activeContext.omradeId ?? '') :
                activeContext.type === 'delomradegruppe' ? `gruppe:${activeContext.gruppeId ?? ''}` :
                    activeContext.type === 'omrade' && activeNavKey === 'delomrade-soner' ? (activeContext.omradeId ?? '') :
                        '';

    void kommune;

    /* ------------------------------------------------------------------ */
    /*  Drag-og-slipp state                                                */
    /* ------------------------------------------------------------------ */

    const isDragEnabled = !!(onMoveOmråde || onCopyVeiledninger);
    const [draggedOmråde, setDraggedOmråde] = useState<Område | null>(null);
    const [forceExpandedIds, setForceExpandedIds] = useState<ReadonlySet<string>>(new Set());
    const [contextExpandedIds, setContextExpandedIds] = useState<ReadonlySet<string>>(new Set());

    useEffect(() => {
        // Et delområde valgt direkte (f.eks. via kartvelger) må få sin
        // delområdetype-node ekspandert, ellers er den aktive leaf-noden skjult
        // og brukeren ser ingen seleksjon i treet. Er delområdet medlem av en
        // gruppe rendres det under gruppe-noden — da må også den ekspanderes.
        if (activeContext.type === 'delomrade' && activeContext.delomradetype) {
            const toExpand = new Set<string>([activeContext.delomradetype]);
            const gruppeId = activeContext.omradeId
                ? memberToGruppe.get(activeContext.omradeId)
                : undefined;
            if (gruppeId) toExpand.add(`gruppe:${gruppeId}`);
            setContextExpandedIds(toExpand);
            return;
        }
        if (activeContext.type !== 'omrade' || !activeContext.omradeId) {
            setContextExpandedIds(new Set());
            return;
        }
        const area = egendefinerte.find((e) => e.id === activeContext.omradeId);
        if (!area) { setContextExpandedIds(new Set()); return; }
        const toExpand = new Set<string>();
        if (area.parentType === 'Delområdetype' && area.parentId) {
            toExpand.add(area.parentId);
        } else if (area.parentType === 'Delområde' && area.parentId) {
            toExpand.add(area.parentId);
            const parentCtx = contextMap.get(area.parentId);
            if (parentCtx?.delomradetype) toExpand.add(parentCtx.delomradetype);
        }
        setContextExpandedIds(toExpand);
    }, [activeContext.type, activeContext.omradeId, activeContext.delomradetype, egendefinerte, contextMap, memberToGruppe]);
    const [dragShowDelomradeTree, setDragShowDelomradeTree] = useState(false);
    const [dragOverId, setDragOverId] = useState<string | null>(null);
    const [dragOverDelomradeSection, setDragOverDelomradeSection] = useState(false);
    const [dropMenu, setDropMenu] = useState<{
        x: number;
        y: number;
        omrade: Område;
        target: OmrådeDragTarget;
    } | null>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sectionHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dropMenuRef = useRef<HTMLDivElement | null>(null);
    const dropPendingRef = useRef(false);

    function clearHoverTimer() {
        if (hoverTimerRef.current !== null) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }

    function clearSectionHoverTimer() {
        if (sectionHoverTimerRef.current !== null) {
            clearTimeout(sectionHoverTimerRef.current);
            sectionHoverTimerRef.current = null;
        }
    }

    const effectiveExpandedIds: ReadonlySet<string> = contextExpandedIds.size === 0
        ? forceExpandedIds
        : forceExpandedIds.size === 0
            ? contextExpandedIds
            : new Set([...forceExpandedIds, ...contextExpandedIds]);

    const dragHandlers: NavDragContextValue = {
        draggedOmråde,
        forceExpandedIds: effectiveExpandedIds,
        dragOverId,
        dropTargetId: dropMenu?.target.parentId ?? null,
        isDragEnabled,

        onEgendefinertDragStart(omrade, e) {
            e.dataTransfer.effectAllowed = 'move';
            e.stopPropagation();
            setDraggedOmråde(omrade);
        },

        onEgendefinertDragEnd() {
            setDraggedOmråde(null);
            setDragOverId(null);
            setDragOverDelomradeSection(false);
            clearHoverTimer();
            clearSectionHoverTimer();
            // Hvis en drop-meny er på vei opp, beholdes treets ekspanderte tilstand
            // og synlighet til brukeren gjør et valg i menyen.
            if (!dropPendingRef.current) {
                setForceExpandedIds(new Set());
                setDragShowDelomradeTree(false);
            }
        },

        onTargetDragEnter(nodeId, e) {
            if (!draggedOmråde) return;
            e.preventDefault();
            e.stopPropagation();
            setDragOverId(nodeId);
            clearHoverTimer();
            hoverTimerRef.current = setTimeout(() => {
                setForceExpandedIds((prev) => {
                    const next = new Set(prev);
                    next.add(nodeId);
                    return next;
                });
            }, 600);
        },

        onTargetDragOver(_target, e) {
            if (!draggedOmråde) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
        },

        onTargetDragLeave(nodeId, e) {
            if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) return;
            setDragOverId((prev) => (prev === nodeId ? null : prev));
            clearHoverTimer();
            // Behold force-ekspandert tilstand til draget avsluttes
            void nodeId;
        },

        onTargetDrop(target, e) {
            if (!draggedOmråde) return;
            e.preventDefault();
            e.stopPropagation();
            clearHoverTimer();
            clearSectionHoverTimer();
            setDragOverId(null);

            // Plasser menyen under selve raden (ikke hele wrapper inkl. barn)
            const wrapper = e.currentTarget as HTMLElement;
            const rowEl = (wrapper.firstElementChild as HTMLElement) ?? wrapper;
            const rect = rowEl.getBoundingClientRect();
            const menuWidth = 303; // 18.9375rem
            const menuHeight = 110;
            const x = Math.min(rect.left, window.innerWidth - menuWidth - 8);
            const y = Math.min(rect.bottom + 4, window.innerHeight - menuHeight - 8);

            dropPendingRef.current = true;
            setDropMenu({ x, y, omrade: draggedOmråde, target });
            setDraggedOmråde(null);
        },
    };

    function clearDropState() {
        dropPendingRef.current = false;
        setDropMenu(null);
        setForceExpandedIds(new Set());
        setDragShowDelomradeTree(false);
    }

    // Lukk drop-menyen ved klikk utenfor
    useEffect(() => {
        if (!dropMenu) return;
        function handleClickOutside(e: MouseEvent) {
            if (dropMenuRef.current && !dropMenuRef.current.contains(e.target as Node)) {
                clearDropState();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropMenu]);

    function handleDropFlytt() {
        if (!dropMenu) return;
        const { omrade, target } = dropMenu;
        clearDropState();
        onMoveOmråde?.(omrade, target).catch(console.error);
    }

    function handleDropKopier() {
        if (!dropMenu) return;
        const { omrade, target } = dropMenu;
        clearDropState();
        onCopyVeiledninger?.(omrade, target);
    }

    function handleNavClick(key: NavKey) {
        if (key === 'innstillinger') {
            onSelect({ type: 'innstillinger', planId });
        } else if (key === 'plan') {
            onSelect({ type: 'plan', planId });
        } else if (key === 'delomrade-soner') {
            // Klikk på toppen åpner gruppe-admin-siden i stedet for å auto-velge
            // første delområdetype. Brukeren navigerer dypere ved å klikke i treet.
            onSelect({ type: 'delomrade-admin', planId });
        } else if (key === 'egendefinerte') {
            // Klikk på toppen åpner egendefinerte-admin-siden i stedet for å
            // auto-velge første område. Brukeren navigerer dypere ved å klikke
            // i treet.
            onSelect({ type: 'egendefinerte-admin', planId });
        }
    }

    function handleTreeSelect(id: string) {
        const ctx = contextMap.get(id);
        if (ctx) onSelect(ctx);
    }

    return (
        <NavDragContext.Provider value={dragHandlers}>
            <div className={styles.root}>
                <SideNav
                    resizable
                    defaultWidth={330}
                    activeId={activeNavKey}
                    onSelect={(id) => handleNavClick(id as NavKey)}
                >
                    <SideNavGroup>
                        {NAV_ENTRIES.map((entry) => {
                            const showFilterAction = entry.key === 'delomrade-soner' && onToggleFilter;
                            const isDelomradeSection = entry.key === 'delomrade-soner';
                            return (
                                <div
                                    key={entry.key}
                                    onDragEnter={isDelomradeSection && isDragEnabled ? (e) => {
                                        if (!draggedOmråde) return;
                                        e.preventDefault();
                                        setDragOverDelomradeSection(true);
                                        clearSectionHoverTimer();
                                        sectionHoverTimerRef.current = setTimeout(() => {
                                            setDragShowDelomradeTree(true);
                                        }, 600);
                                    } : undefined}
                                    onDragOver={isDelomradeSection && isDragEnabled ? (e) => {
                                        if (!draggedOmråde) return;
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'move';
                                    } : undefined}
                                    onDragLeave={isDelomradeSection && isDragEnabled ? (e) => {
                                        if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) return;
                                        setDragOverDelomradeSection(false);
                                        clearSectionHoverTimer();
                                    } : undefined}
                                >
                                    <div className={[
                                        showFilterAction ? styles.navItemWithAction : undefined,
                                        isDelomradeSection && dragOverDelomradeSection ? styles.navSectionDragOver : undefined,
                                    ].filter(Boolean).join(' ') || undefined}>
                                        <SideNavItem id={entry.key} icon={entry.icon}>
                                            {entry.label}
                                        </SideNavItem>
                                        {showFilterAction && (
                                            <div
                                                className={`${styles.navItemAction}${filterOpen || hasFilter ? ` ${styles.navItemActionAlwaysOn}` : ''}`}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                role="presentation"
                                            >
                                                <IconButton
                                                    icon={<FunnelIcon size={16} weight={hasFilter ? 'fill' : 'regular'} />}
                                                    label="Filtrer delområdetyper"
                                                    size="sm"
                                                    active={filterOpen || hasFilter}
                                                    onClick={onToggleFilter}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {entry.key === 'delomrade-soner' && hasFilter && (
                                        <div className={styles.filterChipRow}>
                                            <Chip
                                                onRemove={onClearPlanFilter}
                                                removeLabel="Tøm filter"
                                            >
                                                {hiddenInPlan} av {totalForChip} skjult
                                            </Chip>
                                        </div>
                                    )}

                                    {entry.key === 'delomrade-soner' && (activeNavKey === 'delomrade-soner' || dragShowDelomradeTree) && (
                                        <section
                                            className={styles.treeRegion}
                                            aria-label={entry.label}
                                        >
                                            {loading && areas.length === 0 ? (
                                                <div className={styles.treeLoading}>
                                                    <Loader size="sm" inline label="Laster delområder…" />
                                                </div>
                                            ) : (
                                                <Tree
                                                    activeId={treeActiveId}
                                                    onSelect={handleTreeSelect}
                                                >
                                                    {areas.map((area) => (
                                                        <DelomradetypeNode
                                                            key={area.id}
                                                            area={area}
                                                            grupperForType={grupperByType.get(area.id) ?? []}
                                                            memberToGruppe={memberToGruppe}
                                                            egendefinertByDelområde={egendefinertByDelområde}
                                                            egendefinertForType={egendefinertByDelområdetype.get(area.id) ?? []}
                                                        />
                                                    ))}
                                                </Tree>
                                            )}
                                        </section>
                                    )}

                                    {entry.key === 'egendefinerte' && activeNavKey === 'egendefinerte' && (
                                        <EgendefinertTree
                                            egendefinertTree={egendefinertTree}
                                            activeOmradeId={activeContext.type === 'omrade' ? (activeContext.omradeId ?? '') : ''}
                                            onSelect={(item) => onSelect({ type: 'omrade', planId, omradeId: item.id, omradeNavn: item.navn })}
                                            label={entry.label}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </SideNavGroup>
                </SideNav>
            </div>

            {/* Drop-meny — rendres via portal for å unngå overflow-klipping */}
            {dropMenu && createPortal(
                <div
                    ref={dropMenuRef}
                    className={styles.dropMenu}
                    style={{ top: dropMenu.y, left: dropMenu.x }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <p className={styles.dropMenuHeader}>
                        Flytt til «{dropMenu.target.navn}»
                    </p>
                    {onMoveOmråde && (() => {
                        const draggedHasChildren = egendefinerte?.some(
                            (o) => o.parentType === 'Område' && o.parentId === dropMenu.omrade.id
                        ) ?? false;
                        return (
                            <button
                                type="button"
                                className={styles.dropMenuItem}
                                onClick={handleDropFlytt}
                                disabled={draggedHasChildren}
                            >
                                <FolderSimpleIcon size="1.125rem" weight="duotone" color="var(--accent-default)" style={{ flexShrink: 0 }} />
                                <span>
                                    Flytt hele området
                                    <span className={styles.dropMenuItemDescription}>
                                        {draggedHasChildren
                                            ? 'Flytt underområdene først.'
                                            : 'Mappen legger seg under delområdet, med eiendommer og veiledningspunkter.'}
                                    </span>
                                </span>
                            </button>
                        );
                    })()}
                    {onCopyVeiledninger && (
                        <button
                            type="button"
                            className={styles.dropMenuItem}
                            onClick={handleDropKopier}
                        >
                            <ListDashesIcon size="1.125rem" weight="duotone" color="var(--accent-default)" style={{ flexShrink: 0 }} />
                            <span>
                                Flytt bare veiledningene
                                <span className={styles.dropMenuItemDescription}>Veiledningspunktene blir flyttet til delområdet.</span>
                            </span>
                        </button>
                    )}
                </div>,
                document.body
            )}
        </NavDragContext.Provider>
    );
}
